import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import api from '@/lib/api';
import { Paperclip, FileText, FileType, Trash2, Plus, X, Upload, Check } from 'lucide-react';
import { useConfirmation } from '../shared/ZCard';
import ZLoadingBar from '@/components/shared/ZLoadingBar';
import ZEmptyState from '@/components/shared/ZEmptyState';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { DeleteConfirmDialog } from '@/components/DeleteConfirmDialog';

const getImageUrl = (url) => url?.startsWith('/') ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url;

function DocCard({ doc, onDelete, onClick }) {
  const isImage = doc.file_type === 'image';
  const isPdf = doc.file_type === 'pdf';
  const url = isImage ? getImageUrl(doc.url) : doc.url;

  const inner = (
    <>
      {isImage ? (
        <img src={url} alt={doc.filename} className="h-20 w-full object-cover" />
      ) : (
        <div className="h-20 bg-white/[0.05] flex flex-col items-center justify-center gap-1">
          {isPdf ? <FileText size={20} className="text-[#9B6DD4]" /> : <FileType size={20} className="text-[#9B6DD4]" />}
          <span className="text-[10px] font-mono text-[#b0b4c0]/70 uppercase tracking-wide">{isPdf ? 'PDF' : 'TXT'}</span>
          <span className="text-[9px] font-mono text-[#b0b4c0]/30">tap to open</span>
        </div>
      )}
      <div className="px-2 py-1">
        <p className="text-[10px] font-mono text-[#b0b4c0]/50 truncate">{doc.filename}</p>
      </div>
      <button
        onClick={(e) => { e.stopPropagation(); onDelete(doc.id); }}
        className="absolute top-1 right-1 opacity-0 group-hover/doc:opacity-100 p-0.5 bg-black/60 rounded text-white/50 hover:text-red-400 transition-all"
        data-testid={`delete-doc-${doc.id}`}
      >
        <Trash2 size={10} />
      </button>
    </>
  );

  if (isImage) {
    return (
      <div
        className="shrink-0 w-28 bg-white/[0.03] border border-white/[0.06] rounded-lg overflow-hidden cursor-pointer relative group/doc hover:border-[#6B35B8]/40 transition-colors"
        onClick={onClick}
        data-testid={`doc-card-${doc.id}`}
      >
        {inner}
      </div>
    );
  }

  return (
    <div
      className="shrink-0 w-28 bg-white/[0.03] border border-white/[0.06] rounded-lg overflow-hidden cursor-pointer relative group/doc hover:border-[#6B35B8]/40 transition-colors"
      data-testid={`doc-card-${doc.id}`}
      onClick={onClick}
    >
      {inner}
    </div>
  );
}

export function DocsTab({ tripId, entries, onDocDeleted }) {
  const feedback = useConfirmation();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAttachDialog, setShowAttachDialog] = useState(false);
  const [attachEntryId, setAttachEntryId] = useState('');
  const [attachFile, setAttachFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [lightboxUrl, setLightboxUrl] = useState(null);
  const [pdfBlobUrl, setPdfBlobUrl] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [docDeleteConfirm, setDocDeleteConfirm] = useState({ open: false, id: null, name: '' });

  useEffect(() => { loadDocs(); }, [tripId]);

  const loadDocs = async () => {
    try {
      const { data } = await api.get(`/trips/${tripId}/documents`);
      setDocs(data || []);
    } catch (err) {
      console.error('Failed to load docs:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (docId) => {
    try {
      const doc = docs.find(d => d.id === docId);
      await api.delete(`/trips/${tripId}/documents/${docId}`);
      setDocs(prev => prev.filter(d => d.id !== docId));
      if (doc?.entry_id) onDocDeleted?.(doc.entry_id);
      feedback.confirm();
    } catch {
      feedback.fail();
    }
  };

  const handleAttach = async () => {
    if (!attachEntryId || !attachFile) return;
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', attachFile);
      await api.post(`/trips/${tripId}/entries/${attachEntryId}/documents`, formData);
      feedback.confirm();
      setShowAttachDialog(false);
      setAttachEntryId('');
      setAttachFile(null);
      loadDocs();
    } catch (err) {
      feedback.fail();
    } finally {
      setUploading(false);
    }
  };

  const handleDocClick = (doc) => {
    if (doc.file_type === 'image') {
      setLightboxUrl(getImageUrl(doc.url));
      return;
    }
    const contentUrl = `${process.env.REACT_APP_BACKEND_URL}/api/documents/${doc.id}/content`;
    setPdfBlobUrl(contentUrl);
  };

  const closePdf = () => setPdfBlobUrl(null);

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); if (e.currentTarget.contains(e.relatedTarget)) return; setIsDragging(false); };
  const handleDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setIsDragging(false);
    const files = e.dataTransfer.files;
    if (files?.length > 0) { setAttachFile(files[0]); setShowAttachDialog(true); }
  };

  const groupedDocs = docs.reduce((acc, doc) => {
    if (!acc[doc.entry_id]) acc[doc.entry_id] = [];
    acc[doc.entry_id].push(doc);
    return acc;
  }, {});

  const getEntryTitle = (entryId, fallback) => {
    const entry = entries.find(e => e.id === entryId);
    return entry?.title || fallback || 'Unknown Entry';
  };

  // Sort entries by date for the picker
  const sortedEntries = [...(entries || [])].sort((a, b) => (a.date || '').localeCompare(b.date || ''));

  if (loading) {
    return <div className="px-6 py-12"><ZLoadingBar accent="#10B981" loading={true} /></div>;
  }

  return (
    <div
      className={`px-6 py-6 min-h-[400px] relative transition-colors ${isDragging ? 'bg-[#6B35B8]/5' : ''}`}
      data-testid="docs-tab"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className="absolute inset-0 border-2 border-dashed border-[#6B35B8] rounded-xl flex items-center justify-center bg-[#6B35B8]/5 z-10 pointer-events-none">
          <div className="text-center">
            <Upload size={32} className="mx-auto mb-2 text-[#9B6DD4]" />
            <p className="text-sm text-[#9B6DD4] font-medium">Drop file to attach</p>
          </div>
        </div>
      )}

      <div className="flex justify-between items-center mb-6">
        <p className="text-xs font-mono text-[#b0b4c0]/50 tracking-wider">Drag & drop files or use Attach</p>
        <Button
          onClick={() => setShowAttachDialog(true)}
          variant="outline"
          className="border-white/[0.06] text-[#b0b4c0] hover:text-[#f0f0f5] hover:bg-white/[0.03] rounded-full text-xs px-4"
          data-testid="attach-doc-btn"
        >
          <Plus size={14} className="mr-1.5" /> Attach
        </Button>
      </div>

      {docs.length === 0 && !isDragging && (
        <div data-testid="docs-empty">
          <ZEmptyState mark="G" accent="#10B981" message="No documents yet." hint="Drop files here to get started" />
        </div>
      )}

      {Object.keys(groupedDocs).length > 0 && (
        <div className="space-y-6">
          {Object.entries(groupedDocs).map(([entryId, entryDocs]) => (
            <div key={entryId}>
              <p className="text-[10px] font-mono text-[#b0b4c0]/50 tracking-wider uppercase mb-3">
                {getEntryTitle(entryId, entryDocs[0]?.entry_title)}
              </p>
              <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide">
                {entryDocs.map(doc => (
                  <DocCard key={doc.id} doc={doc} onDelete={(id) => setDocDeleteConfirm({ open: true, id, name: doc.filename || 'this file' })} onClick={() => handleDocClick(doc)} />
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Attach Dialog — entry list replaces dropdown so user can scroll & pick */}
      <Dialog open={showAttachDialog} onOpenChange={(open) => { setShowAttachDialog(open); if (!open) { setAttachEntryId(''); setAttachFile(null); } }}>
        <DialogContent className="bg-white/[0.03] border-white/[0.06] text-[#f0f0f5] max-w-md" aria-describedby="attach-desc">
          <DialogHeader>
            <DialogTitle className="font-serif text-lg">Attach Document</DialogTitle>
          </DialogHeader>
          <p id="attach-desc" className="sr-only">Attach a document to a trip entry</p>
          <div className="space-y-4 mt-2">

            {/* File picker */}
            <div>
              <label className="text-xs text-[#b0b4c0]/70 mb-2 block">File</label>
              <div
                className={`border-2 border-dashed rounded-lg p-4 text-center cursor-pointer transition-colors ${attachFile ? 'border-[#6B35B8]/40 bg-[#6B35B8]/5' : 'border-white/[0.06] hover:border-[#6B35B8]/40'}`}
                onClick={() => document.getElementById('attach-file-input')?.click()}
                onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('border-[#6B35B8]', 'bg-[#6B35B8]/10'); }}
                onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#6B35B8]', 'bg-[#6B35B8]/10'); }}
                onDrop={(e) => { e.preventDefault(); e.currentTarget.classList.remove('border-[#6B35B8]', 'bg-[#6B35B8]/10'); const f = e.dataTransfer.files?.[0]; if (f) setAttachFile(f); }}
                data-testid="attach-dropzone"
              >
                {attachFile ? (
                  <div className="flex items-center justify-center gap-2">
                    <Paperclip size={14} className="text-[#9B6DD4]" />
                    <span className="text-xs text-[#f0f0f5] truncate max-w-[200px]">{attachFile.name}</span>
                    <button onClick={(e) => { e.stopPropagation(); setAttachFile(null); }} className="text-[#b0b4c0]/50 hover:text-red-400"><X size={12} /></button>
                  </div>
                ) : (
                  <>
                    <Upload size={20} className="mx-auto mb-1 text-[#b0b4c0]/70" />
                    <p className="text-xs text-[#b0b4c0]/70">Drop file or click to select</p>
                    <p className="text-[10px] text-[#b0b4c0]/40 mt-1">Images, PDF, or text files</p>
                  </>
                )}
              </div>
              <input id="attach-file-input" type="file" accept="image/*,application/pdf,text/plain" className="hidden" onChange={(e) => setAttachFile(e.target.files?.[0] || null)} />
            </div>

            {/* Entry picker — scrollable list, no dropdown */}
            <div>
              <label className="text-xs text-[#b0b4c0]/70 mb-2 block">Attach to entry</label>
              <div className="max-h-52 overflow-y-auto rounded-lg border border-white/[0.06] divide-y divide-white/[0.06]" data-testid="attach-entry-select">
                {sortedEntries.map(e => (
                  <button
                    key={e.id}
                    onClick={() => setAttachEntryId(e.id)}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors ${attachEntryId === e.id ? 'bg-[#6B35B8]/20 text-[#f0f0f5]' : 'text-[#b0b4c0] hover:bg-white/[0.03] hover:text-[#f0f0f5]'}`}
                  >
                    {attachEntryId === e.id && <Check size={12} className="text-[#9B6DD4] shrink-0" />}
                    {attachEntryId !== e.id && <div className="w-3 shrink-0" />}
                    <span className="text-[10px] font-mono text-[#b0b4c0]/50 shrink-0 w-20">{e.date || '—'}</span>
                    <span className="text-xs truncate">{e.title || 'Untitled'}</span>
                  </button>
                ))}
              </div>
            </div>

            <Button
              onClick={handleAttach}
              disabled={!attachEntryId || !attachFile || uploading}
              className="w-full bg-[#6B35B8] hover:bg-[#6B35B8]/90 text-white rounded-full"
              data-testid="attach-save"
            >
              {uploading ? 'Uploading…' : 'Attach'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <DeleteConfirmDialog
        open={docDeleteConfirm.open}
        onClose={() => setDocDeleteConfirm({ open: false, id: null, name: '' })}
        onConfirm={() => handleDelete(docDeleteConfirm.id)}
        itemName={docDeleteConfirm.name}
        itemType="document"
      />

      {/* Image lightbox */}
      {lightboxUrl && createPortal(
        <div
          className="fixed inset-0 z-[9999] bg-black/95 flex items-center justify-center p-4"
          onClick={() => setLightboxUrl(null)}
          data-testid="doc-lightbox"
        >
          <button className="absolute top-4 right-4 text-white/70 hover:text-white p-2" onClick={() => setLightboxUrl(null)}>
            <X size={24} />
          </button>
          <img
            src={lightboxUrl}
            alt=""
            className="max-w-full max-h-full object-contain rounded-lg shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}

      {/* PDF viewer */}
      {pdfBlobUrl && createPortal(
        <div className="fixed inset-0 z-[9999] bg-[#0a0a0f] flex flex-col" data-testid="pdf-viewer">
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06] flex-shrink-0">
            <span className="text-[10px] font-mono text-[#b0b4c0]/50 tracking-widest uppercase">Document</span>
            <button className="text-white/50 hover:text-white p-1 transition-colors" onClick={closePdf}>
              <X size={18} />
            </button>
          </div>
          <embed src={pdfBlobUrl} type="application/pdf" className="flex-1 w-full" />
        </div>,
        document.body
      )}
    </div>
  );
}
